import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database'
import { KNOWN_ORGS } from '../config/organizations'

// Environment variables with validation
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable')
}

if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable')
}

// Create Supabase client with TypeScript types
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Extend session timeout
    storageKey: 'facility-pro-auth',
    storage: {
      getItem: (key) => {
        if (typeof window !== 'undefined') {
          return window.localStorage.getItem(key)
        }
        return null
      },
      setItem: (key, value) => {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, value)
        }
      },
      removeItem: (key) => {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(key)
        }
      }
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'facility-pro@1.0.0'
    }
  }
})

// Helper function to handle auth errors
export const handleAuthError = (error: { message?: string } | null) => {
  // Handle auth error without logging
  
  if (error?.message) {
    // Common auth error messages to user-friendly text
    switch (error.message) {
      case 'Invalid login credentials':
        return 'Invalid email or password. Please try again.'
      case 'Email not confirmed':
        return 'Please check your email and click the confirmation link.'
      case 'User not found':
        return 'No account found with this email address.'
      case 'Password should be at least 6 characters':
        return 'Password must be at least 6 characters long.'
      default:
        return error.message
    }
  }
  
  return 'An unexpected error occurred. Please try again.'
}

// Helper function to check if user session is valid
export const isValidSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()
    return !error && !!session
  } catch {
    return false
  }
}

// Background helper functions for profile loading
async function refreshProfileInBackground(userId: string) {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (profile) {
      const profileWithTimestamp = {
        ...profile,
        cached_at: new Date().toISOString()
      }
      localStorage.setItem('cached_profile', JSON.stringify(profileWithTimestamp))
    }
  } catch (e) {
    // Silently fail - this is background
  }
}

async function loadFullProfileInBackground(userId: string) {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    
    if (profile) {
      const profileWithTimestamp = {
        ...profile,
        cached_at: new Date().toISOString()
      }
      localStorage.setItem('cached_profile', JSON.stringify(profileWithTimestamp))
    }
  } catch (e) {
    // Silently fail - this is background
  }
}

// Helper to add timeout to promises
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, timeoutError: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => 
      setTimeout(() => reject(new Error(timeoutError)), timeoutMs)
    )
  ])
}

// Helper function to get current user profile
export const getCurrentUserProfile = async () => {
  try {
    console.log('🔐 Getting current user profile...')
    
    // Check cache first for performance
    const cachedProfile = localStorage.getItem('cached_profile');
    if (cachedProfile) {
      try {
        const parsed = JSON.parse(cachedProfile);
        if (parsed.id) {
          const cacheAge = Date.now() - new Date(parsed.cached_at || parsed.updated_at || 0).getTime();
          
          // Use cache if less than 5 minutes old
          if (cacheAge < 5 * 60 * 1000) {
            console.log('🔐 Using cached profile - age:', Math.round(cacheAge / 1000), 'seconds');
            
            // Refresh in background if older than 1 minute
            if (cacheAge > 60 * 1000) {
              refreshProfileInBackground(parsed.id);
            }
            
            return parsed;
          } else {
            console.log('🔐 Cache too old, fetching fresh profile')
          }
        }
      } catch (e) {
        console.log('🔐 Invalid cache, fetching fresh profile')
      }
    }
    
    // Try session first as it's more reliable
    console.log('🔐 Getting session first...')
    let user = null;
    
    try {
      const { data: { session }, error: sessionError } = await withTimeout(
        supabase.auth.getSession(),
        10000,
        'Session timeout after 10 seconds'
      );
      
      if (!sessionError && session?.user) {
        console.log('🔐 Got user from session:', session.user.email)
        user = session.user;
      }
    } catch (e) {
      console.error('🔐 Session failed:', e.message)
    }
    
    // If session failed, try getUser
    if (!user) {
      console.log('🔐 Falling back to getUser...')
      try {
        const { data: { user: authUser }, error: userError } = await withTimeout(
          supabase.auth.getUser(),
          10000,
          'Auth getUser timeout after 10 seconds'
        );
        
        if (!userError && authUser) {
          user = authUser;
        }
      } catch (e) {
        console.error('🔐 getUser failed:', e.message)
      }
    }
    
    if (!user) {
      console.log('🔐 No authenticated user found')
      // Check if we have a cached profile we can use
      if (cachedProfile) {
        try {
          const parsed = JSON.parse(cachedProfile);
          console.log('🔐 Using stale cached profile as fallback')
          return parsed;
        } catch {}
      }
      return null
    }
    
    console.log('🔐 User found, fetching profile for:', user.email)

    // Skip RPC functions for now - they might not exist
    console.log('🔐 Skipping RPC functions, using direct query')

    // Fallback to direct query
    console.log('🔐 Querying profiles table for user:', user.id)
    const { data: profileData, error } = await withTimeout(
      supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single(),
      5000,
      'Profile query timeout after 5 seconds'
    )

    // Debug log the raw profile data
    console.log('🔐 Raw profile data:', profileData)
    
    // Map primary_organization_id to organization_id if it exists
    let profile = profileData
    if (profile && profile.primary_organization_id) {
      console.log('🔐 Mapping primary_organization_id to organization_id:', profile.primary_organization_id)
      profile = {
        ...profile,
        organization_id: profile.primary_organization_id
      }
    }
    
    if (error) {
      console.warn('Profile fetch error:', error.code, error.message)
      
      // Check localStorage for cached profile
      try {
        const cached = localStorage.getItem('cached_profile')
        if (cached) {
          const cachedProfile = JSON.parse(cached)
          if (cachedProfile.id === user.id) {
            console.log('🔐 Using cached profile due to database error')
            return cachedProfile
          }
        }
      } catch {}
      
      // Return a default profile structure for authenticated users
      // Use organization from user metadata
      const metadataOrgId = (user as unknown as { user_metadata?: { organization_id?: string } }).user_metadata?.organization_id
      const defaultOrgId = metadataOrgId || KNOWN_ORGS.DEFAULT

      const defaultProfile = {
        id: user.id,
        email: user.email || '',
        first_name: user.email?.split('@')[0] || '',
        last_name: '',
        full_name: user.email || '',
        role: 'admin', // Default to admin to prevent access issues
        organization_id: defaultOrgId,
        primary_organization_id: defaultOrgId, // Include both for compatibility
        is_super_admin: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        cached_at: new Date().toISOString()
      }
      
      // Cache even the default profile
      localStorage.setItem('cached_profile', JSON.stringify(defaultProfile))
      
      return defaultProfile
    }

    // Note: profile.role is deprecated. Role is now stored in organization_users table.
    // The AuthContext fetches organizationRole from organization_users directly.
    // This code block is removed to prevent silent fallbacks to admin role.

    // Cache the profile
    const profileWithTimestamp = {
      ...profile,
      cached_at: new Date().toISOString()
    }
    localStorage.setItem('cached_profile', JSON.stringify(profileWithTimestamp))
    
    // Start background refresh
    setTimeout(() => refreshProfileInBackground(user.id), 60000)

    console.log('🔐 Successfully loaded profile:', profile.email)
    return profile
  } catch (error) {
    console.error('🔐 Profile loading error:', error)
    // Check if it's a timeout
    if (error.message && error.message.includes('timeout')) {
      console.error('🔐 Operation timed out - database might be slow or unresponsive')
    }
    return null
  }
}